import { useEffect, useMemo, useState } from 'react';
import { useAuth, useSupabase } from '../providers/SupabaseProvider';

interface WorkflowAccessState {
  loading: boolean;
  hasAccess: boolean;
  organizationId: string | null;
}

/**
 * Hook that checks whether the current user has access to workflow features.
 * Access is granted only if the organization subscription includes the workflow feature.
 */
export function useWorkflowFeatureAccess(providedOrganizationId?: string | null): WorkflowAccessState {
  const { user } = useAuth();
  const supabase = useSupabase();
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(providedOrganizationId ?? null);

  useEffect(() => {
    if (providedOrganizationId) {
      setOrganizationId(providedOrganizationId);
    }
  }, [providedOrganizationId]);

  useEffect(() => {
    let isMounted = true;

    async function resolveOrganizationId() {
      if (!user?.id) {
        if (isMounted) {
          setOrganizationId(null);
          setLoading(false);
          setHasAccess(false);
        }
        return;
      }

      if (organizationId) {
        return;
      }

      try {
        const { data, error } = await supabase
          .from('organization_users')
          .select('organization_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) throw error;
        if (isMounted) {
          setOrganizationId(data?.organization_id ?? null);
        }
      } catch (err) {
        console.error('Fehler beim Laden der Organisation für Workflow-Zugriff:', err);
        if (isMounted) {
          setOrganizationId(null);
        }
      }
    }

    resolveOrganizationId();
  }, [organizationId, supabase, user?.id]);

  useEffect(() => {
    let isMounted = true;

    async function checkAccess(currentOrganizationId: string | null) {
      if (!user?.id || !currentOrganizationId) {
        if (isMounted) {
          setHasAccess(false);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        const { data: organizationFeatures, error: organizationFeaturesError } = await supabase
          .from('organization_features')
          .select('feature_workflow')
          .eq('organization_id', currentOrganizationId)
          .maybeSingle();

        if (organizationFeaturesError) throw organizationFeaturesError;

        const hasOrganizationWorkflow = Boolean(organizationFeatures?.feature_workflow);

        if (!hasOrganizationWorkflow) {
          if (isMounted) {
            setHasAccess(false);
          }
          return;
        }

        if (isMounted) {
          setHasAccess(hasOrganizationWorkflow);
        }
      } catch (err) {
        console.error('Fehler beim Prüfen des Workflow-Zugriffs:', err);
        if (isMounted) {
          setHasAccess(false);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    checkAccess(organizationId);

    return () => {
      isMounted = false;
    };
  }, [organizationId, supabase, user?.id]);

  return useMemo(
    () => ({
      loading,
      hasAccess,
      organizationId,
    }),
    [hasAccess, loading, organizationId],
  );
}

export default useWorkflowFeatureAccess;
